import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Shared";
import { useAuth } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { useLiveRefresh } from "@/lib/live";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  FileBarChart, Coins, Repeat2, AlertTriangle, Percent, Recycle, Clock,
  ListOrdered, UserCog, Truck, PieChart, TrendingUp, Users, Link2, Filter,
  ArrowLeftRight, RefreshCw,
} from "lucide-react";

import MaterialPendingReport from "@/components/reports/MaterialPendingReport";
import InventoryValueReport from "@/components/reports/InventoryValueReport";
import ConsumptionVsPurchaseReport from "@/components/reports/ConsumptionVsPurchaseReport";
import StockAlertsReport from "@/components/reports/StockAlertsReport";
import JobwiseRmPctReport from "@/components/reports/JobwiseRmPctReport";
import WastageReport from "@/components/reports/WastageReport";
import JobsAgingReport from "@/components/reports/JobsAgingReport";
import JobsInHandReport from "@/components/reports/JobsInHandReport";
import OperatorOutputReport from "@/components/reports/OperatorOutputReport";
import OTIFReport from "@/components/reports/OTIFReport";
import RMPercentageDetailReport from "@/components/reports/RMPercentageDetailReport";
import InflowVsDispatchReport from "@/components/reports/InflowVsDispatchReport";
import NewVsRepeatReport from "@/components/reports/NewVsRepeatReport";
import DependencyListReport from "@/components/reports/DependencyListReport";
import InquiryConversionReport from "@/components/reports/InquiryConversionReport";
import StageWasteTrendReport from "@/components/reports/StageWasteTrendReport";
import StoresIssueRegisterReport from "@/components/reports/StoresIssueRegisterReport";

// One tab per report — kept as an array so adding a new report is a one-liner
// (add a file under /components/reports/, import above, drop an entry here).
const ALL_TABS = [
  { key: "material-pending",  label: "Material-wise Pending",   Icon: FileBarChart,    Comp: MaterialPendingReport },
  { key: "inv-value",         label: "Inventory Value & Age",   Icon: Coins,           Comp: InventoryValueReport },
  { key: "inv-consumption",   label: "Consumption vs Purchase", Icon: Repeat2,         Comp: ConsumptionVsPurchaseReport },
  { key: "inv-alerts",        label: "Stock Alerts",            Icon: AlertTriangle,   Comp: StockAlertsReport },
  { key: "rm-pct",            label: "Jobwise RM %",            Icon: Percent,         Comp: JobwiseRmPctReport },
  { key: "wastage",           label: "Wastage",                 Icon: Recycle,         Comp: WastageReport },
  { key: "jobs-aging",        label: "Jobs Aging",              Icon: Clock,           Comp: JobsAgingReport },
  { key: "jobs-in-hand",      label: "Jobs in Hand (Priority)", Icon: ListOrdered,     Comp: JobsInHandReport },
  { key: "op-output",         label: "Operator Output",         Icon: UserCog,         Comp: OperatorOutputReport },
  { key: "otif",              label: "OTIF / Late Delivery",    Icon: Truck,           Comp: OTIFReport },
  { key: "rm-detail",         label: "RM % Detail",             Icon: PieChart,        Comp: RMPercentageDetailReport },
  { key: "inflow",            label: "Inflow vs Dispatch",      Icon: TrendingUp,      Comp: InflowVsDispatchReport },
  { key: "new-repeat",        label: "New vs Repeat %",         Icon: Users,           Comp: NewVsRepeatReport },
  { key: "dependencies",      label: "Dependency List",         Icon: Link2,           Comp: DependencyListReport },
  { key: "inquiry-conv",      label: "Inquiry Conversion",      Icon: Filter,          Comp: InquiryConversionReport },
  { key: "stage-waste-trend", label: "Stage Waste Trend",       Icon: Recycle,         Comp: StageWasteTrendReport },
  { key: "stores-issue-reg",  label: "Stores Issue Register",   Icon: ArrowLeftRight,  Comp: StoresIssueRegisterReport },
];

// Reports that show money (stock value, purchase cost, RM % of revenue): hidden for views without finance
const MONEY_TABS = ["inv-value", "inv-consumption", "rm-pct", "rm-detail"];

export default function Reports() {
  const { user } = useAuth();
  const TABS = ALL_TABS.filter((t) => viewFor(user).finance || !MONEY_TABS.includes(t.key));
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : TABS[0].key;
  // A report is read as a whole, so it doesn't redraw under the reader: changes by
  // anyone raise a bar, and Refresh re-runs the open report (filters reset).
  const [changed, setChanged] = useState(false);
  const [run, setRun] = useState(0);
  useLiveRefresh(["jobs", "stores", "production", "customers", "finance"], () => setChanged(true));
  const refresh = () => { setChanged(false); setRun((n) => n + 1); };
  const openTab = (key) => { setChanged(false); setParams({ tab: key }, { replace: true }); };

  return (
    <div>
      <PageHeader subtitle="Analytics & Reports" title="Reports" subtitleKey="page.reports.subtitle" titleKey="page.reports.title" />
      <div className="p-8">
        {changed && (
          <div className="mb-4 flex items-center justify-between gap-3 border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="reports-changed">
            <span className="flex items-center gap-2"><RefreshCw size={14} /> Figures have changed since this report was opened.</span>
            <button type="button" onClick={refresh} className="font-semibold underline">Refresh report</button>
          </div>
        )}
        <Tabs value={tab} onValueChange={openTab} className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200 flex-wrap h-auto">
            {TABS.map(({ key, label, Icon }) => (
              <TabsTrigger key={key} value={key} data-testid={`report-tab-${key}`} className="rounded-sm">
                <Icon size={13} className="mr-1.5" /> {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(({ key, Comp }) => (
            <TabsContent key={key} value={key} className="mt-4">
              <Comp key={run} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
